import { useState, useEffect, useCallback } from 'react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/toastContext';
import { useConfirm } from '../components/ui/useConfirm';
import { RefreshCw, AlertTriangle, Loader2, Server, Activity, Box, Clock } from 'lucide-react';

interface ContainerInfo {
  name: string;
  status: string;
  port: number;
  image: string;
}

interface GCStats {
  total_containers: number;
  running: number;
  exited: number;
  other: number;
  containers: ContainerInfo[];
}

interface GCPolicy {
  max_runtime_hours: number;
  idle_shutdown_minutes: number;
  error_cleanup_minutes: number;
}

export default function DockerGCAdmin() {
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [stats, setStats] = useState<GCStats | null>(null);
  const [policy, setPolicy] = useState<GCPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [gcRunning, setGcRunning] = useState(false);
  const [cleanupRunning, setCleanupRunning] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, policyRes] = await Promise.all([
        apiClient.get('/admin/gc/stats'),
        apiClient.get('/admin/gc/policy'),
      ]);
      setStats(statsRes.data);
      setPolicy(policyRes.data);
    } catch (e) {
      console.error('Failed to load GC data:', e);
      toast.error('加载数据失败');
    }
  }, [toast]);

  // 手动刷新/操作后重载：展示整页 loading（事件回调中同步 setState 是允许的）
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      await fetchData();
    } finally {
      setLoading(false);
    }
  }, [fetchData]);

  useEffect(() => {
    // 初始 loading 已为 true；effect 内使用内联 promise 链，避免被判定为同步 setState
    Promise.all([apiClient.get('/admin/gc/stats'), apiClient.get('/admin/gc/policy')])
      .then(([statsRes, policyRes]) => {
        setStats(statsRes.data);
        setPolicy(policyRes.data);
      })
      .catch((e) => {
        console.error('Failed to load GC data:', e);
        toast.error('加载数据失败');
      })
      .finally(() => setLoading(false));
  }, [toast]);

  const triggerGC = async () => {
    setGcRunning(true);
    try {
      const res = await apiClient.post('/admin/gc');
      const summary = res.data.summary;
      toast.success(
        `GC 完成: 停止 ${summary.runtime_stopped}, 回收 ${summary.runtime_shutdown}, 清理孤儿 ${summary.orphans_removed}, 清理退出 ${summary.exited_removed}`
      );
      await loadData();
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
      const detail = err.response?.data?.detail;
      toast.error('GC 失败: ' + (typeof detail === 'string' ? detail : (err.message as string)));
    } finally {
      setGcRunning(false);
    }
  };

  const emergencyCleanup = async () => {
    if (!(await confirm({
      title: '确定要执行紧急清理吗？',
      message: '这将停止并删除所有 Docker 容器！',
      danger: true,
      requireText: '清理',
    }))) return;
    setCleanupRunning(true);
    try {
      const res = await apiClient.post('/admin/gc/cleanup-all');
      toast.success(`紧急清理完成: 删除 ${res.data.removed}/${res.data.total} 个容器`);
      await loadData();
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
      const detail = err.response?.data?.detail;
      toast.error('清理失败: ' + (typeof detail === 'string' ? detail : (err.message as string)));
    } finally {
      setCleanupRunning(false);
    }
  };

  const updatePolicy = async (key: keyof GCPolicy, value: number) => {
    if (!policy) return;
    const newPolicy = { ...policy, [key]: value };
    try {
      await apiClient.put('/admin/gc/policy', newPolicy);
      setPolicy(newPolicy);
      toast.success('策略已更新');
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
      const detail = err.response?.data?.detail;
      toast.error('更新失败: ' + (typeof detail === 'string' ? detail : (err.message as string)));
    }
  };

  const statusColor = (status: string) => {
    if (status === 'running') return 'text-success';
    if (status === 'exited' || status === 'dead') return 'text-error';
    return 'text-warning';
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="flex items-center justify-between mb-4">
        <h1><Box className="inline mr-2" size={24} /> Docker 垃圾回收</h1>
        <button className="secondary" onClick={loadData}><RefreshCw size={16} className="inline mr-1" /> 刷新</button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="card">
            <div className="flex items-center gap-2 mb-2">
              <Server size={20} className="text-primary" />
              <span className="text-sm text-muted">总容器数</span>
            </div>
            <div className="text-2xl font-bold">{stats.total_containers}</div>
          </div>
          <div className="card">
            <div className="flex items-center gap-2 mb-2">
              <Activity size={20} className="text-success" />
              <span className="text-sm text-muted">运行中</span>
            </div>
            <div className="text-2xl font-bold">{stats.running}</div>
          </div>
          <div className="card">
            <div className="flex items-center gap-2 mb-2">
              <Box size={20} className="text-error" />
              <span className="text-sm text-muted">已退出</span>
            </div>
            <div className="text-2xl font-bold">{stats.exited}</div>
          </div>
          <div className="card">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={20} className="text-warning" />
              <span className="text-sm text-muted">其他</span>
            </div>
            <div className="text-2xl font-bold">{stats.other}</div>
          </div>
        </div>
      )}

      {/* Policy Settings */}
      {policy && (
        <div className="card mb-6">
          <h3 className="mb-3">回收策略配置</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
                最大运行时长 (小时)
              </label>
              <input
                type="number"
                step="0.5"
                value={policy.max_runtime_hours}
                onChange={e => updatePolicy('max_runtime_hours', parseFloat(e.target.value))}
              />
              <p className="text-xs text-muted mt-1">RUNNING 超过此时长自动停止</p>
            </div>
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
                空闲回收时间 (分钟)
              </label>
              <input
                type="number"
                value={policy.idle_shutdown_minutes}
                onChange={e => updatePolicy('idle_shutdown_minutes', parseFloat(e.target.value))}
              />
              <p className="text-xs text-muted mt-1">STOPPED 超过此时长自动释放</p>
            </div>
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
                错误清理时间 (分钟)
              </label>
              <input
                type="number"
                value={policy.error_cleanup_minutes}
                onChange={e => updatePolicy('error_cleanup_minutes', parseFloat(e.target.value))}
              />
              <p className="text-xs text-muted mt-1">ERROR 超过此时长自动释放</p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 mb-6">
        <button className="primary" onClick={triggerGC} disabled={gcRunning}>
          {gcRunning ? <><Loader2 size={16} className="inline animate-spin mr-1" /> GC 运行中...</> : <><RefreshCw size={16} className="inline mr-1" /> 立即回收</>}
        </button>
        <button className="secondary" onClick={emergencyCleanup} disabled={cleanupRunning} style={{ borderColor: 'var(--color-error)', color: 'var(--color-error)' }}>
          {cleanupRunning ? <><Loader2 size={16} className="inline animate-spin mr-1" /> 清理中...</> : <><AlertTriangle size={16} className="inline mr-1" /> 紧急清理全部</>}
        </button>
      </div>

      {/* Container List */}
      <div className="card">
        <h3 className="mb-3">容器列表</h3>
        {stats && stats.containers.length === 0 ? (
          <div className="empty">
            <p className="text-muted">暂无 managed 容器</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th>名称</th>
                  <th>状态</th>
                  <th>端口</th>
                  <th>镜像</th>
                </tr>
              </thead>
              <tbody>
                {stats?.containers.map(c => (
                  <tr key={c.name}>
                    <td>{c.name}</td>
                    <td className={statusColor(c.status)}>{c.status}</td>
                    <td>{c.port || '-'}</td>
                    <td className="text-muted">{c.image}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {dialog}
    </div>
  );
}
